import apiClient from '../../shared/services/apiClient';

/**
 * Cart Service (User Version)
 *
 * Handles shopping cart operations for users.
 * Users can add items, update quantities, remove items, and checkout.
 *
 * All routes use /api/user/cart/* prefix
 */
export const cartService = {
    /**
     * Get cart contents
     */
    getCart: async () => {
        try {
            const response = await apiClient.get('/api/user/cart');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Add item to cart (new format, supports product_type)
     * @param {Object} data - { product_type, product_id, quantity, billing_cycle }
     */
    addItem: async (data) => {
        try {
            const response = await apiClient.post('/api/user/cart/add', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Upgrade / downgrade an existing subscription via the cart.
     * @param {Object} data - { subscription_id, product_id, billing_cycle? }
     * @returns {Object} { success, data: { cart_item_id }, code?, conflicts? }
     */
    addChange: async (data) => {
        try {
            const response = await apiClient.post('/api/user/cart/add-change', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Add domain to cart
     * @param {string} domainName - Domain name (e.g., example.com)
     * @param {string} action - Action type: register/transfer/renew
     * @param {number} years - Number of years (1-10)
     * @param {number} registrarId - Optional registrar ID
     */
    addDomain: async (domainName, action = 'register', years = 1, registrarId = null, extra = {}) => {
        try {
            const data = {
                domain_name: domainName,
                action,
                years,
                ...(extra || {})
            };
            if (registrarId) {
                data.registrar_id = registrarId;
            }
            const response = await apiClient.post('/api/user/cart/add-domain', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Update cart item quantity
     * @param {number} itemId - Cart item ID
     * @param {number} quantity - New quantity
     */
    updateCartItem: async (itemId, quantity) => {
        try {
            const response = await apiClient.post('/api/user/cart/update', {
                item_id: itemId,
                quantity
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Remove item from cart
     * @param {number} itemId - Cart item ID
     */
    removeFromCart: async (itemId) => {
        try {
            const response = await apiClient.post('/api/user/cart/remove', {
                item_id: itemId
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Clear cart
     */
    clearCart: async () => {
        try {
            const response = await apiClient.post('/api/user/cart/clear');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Validate a coupon code against current cart contents
     * @param {string} code - Coupon code
     * @returns {Object} { success, data: { discount, eligible_amount, cart_total, total_after_discount, ... } }
     */
    /** Wallet coupons with applicability against the current cart */
    getWalletCoupons: async () => {
        try {
            const response = await apiClient.get('/api/user/cart/coupons');
            return response.data;
        } catch (error) {
            return { success: false, data: [] };
        }
    },

    validateCoupon: async (code) => {
        try {
            const response = await apiClient.post('/api/user/cart/validate-coupon', { code });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * Checkout cart
     * @param {Array} domainConfigs - Optional domain configurations for domain items
     *   Each config: { cart_item_id, domain, contact_id, nameservers, auto_renew, whois_privacy, additional_info }
     * @param {string} couponCode - Optional coupon code (re-validated server-side)
     */
    checkout: async (domainConfigs = null, couponCode = null, autoRenew = true) => {
        try {
            const data = { auto_renew: autoRenew ? 1 : 0 };
            if (domainConfigs && domainConfigs.length > 0) {
                data.domain_configs = domainConfigs;
            }
            if (couponCode) {
                data.coupon_code = couponCode;
            }
            const response = await apiClient.post('/api/user/cart/checkout', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    }
};

export default cartService;
